import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { class: "text-subtitle2 q-mb-sm" }

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxCardWorkOrderForEntity from "src/components/card/SfxCardWorkOrderForEntity.vue"
import SfxSkeleton from "src/components/special/SfxSkeleton.vue"
import router from "src/router"
import { Log } from "src/util/classes/log"
import { NamedPath } from "src/util/enums/namedPaths"
import { checkScan } from "src/util/functions/identifier"
import {
  showErrorNotification,
  showNotImplementedNotification,
} from "src/util/functions/notifications"
import { getCorrectiveWorkOnItemListForWorkOrder } from "src/util/functions/performedWork"
import { getWorkDomainWorkspaceData } from "src/util/functions/workDomain"
import { IssueAssociationType } from "src/util/types/dataModels/issue"
import { CorrectiveWorkOnItem } from "src/util/types/dataModels/performedWork"
import { WorkOrderCardData } from "src/util/types/dataModels/workOrder"
import { computed, onMounted, PropType, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useFilterStore } from "src/stores/filterStore"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogListWorkOrder',
  props: {
  associationType: {
    type: String as PropType<IssueAssociationType>,
    required: true,
  },

  workOrders: {
    type: Array as PropType<WorkOrderCardData[]>,
    required: true,
  },

  entityId: {
    type: String,
    required: true,
  },

  entityName: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()
const { dialogRef, onDialogCancel } = useDialogPluginComponent()
const loadingData = ref(true)

const notPerformedWorkOrderList = ref<WorkOrderCardData[]>([])
const performedWorkOrderList = ref<WorkOrderCardData[]>([])
const currentSelection = ref<WorkOrderCardData | undefined>(undefined)

const onlyOneWorkOrder = computed(() => props.workOrders?.length === 1)

const subtitle = computed(() =>
  onlyOneWorkOrder.value
    ? t("sfxDialogListWorkOrder.text_only_one_work_order")
    : t("sfxDialogListWorkOrder.text_more_than_one_work_order")
)

const leftButtonText = computed(() =>
  currentSelection.value ? t("sfxButtons.btn_open_work_orders") : t("sfxButtons.btn_cancel")
)

const leftButtonFunction = computed(() => (currentSelection.value ? openWorkOrder : clickCancel))

const rightButtonText = computed(() => getRightButtonText())

//endregion

//region Lifecycle

onMounted(async () => {
  await assignWorkOrderCardData()

  if (onlyOneWorkOrder.value) {
    currentSelection.value =
      notPerformedWorkOrderList.value.length === 1
        ? notPerformedWorkOrderList.value[0]
        : performedWorkOrderList.value[0]
  }

  loadingData.value = false
})

//endregion

//region Functions

async function assignWorkOrderCardData() {
  for (const workOrder of props.workOrders) {
    // Assign CorrectiveWorkOrderCardData
    if (workOrder.isCorrectiveWork) {
      const correctiveWorkList = await getCorrectiveWorkOnItemListForWorkOrder(
        workOrder.workOrderData.id
      )
      if (correctiveWorkList && correctiveWorkList.length > 0) {
        assignCorrectiveWorkOrderCardData(workOrder, correctiveWorkList)
      }

      // Assign RegularWorkOrderCardData
    } else {
      // A RegularWorkOrder can have many Items but only one RegularWork per Item
      const conclusion = workOrder.performedWorkData[0]?.conclusion

      // To be in the `Performed` group the RegularPerformedWork done in the WorkOrder
      // should be in the `accepted` state
      if (conclusion && conclusion === "accepted") {
        performedWorkOrderList.value.push(workOrder)
      } else {
        notPerformedWorkOrderList.value.push(workOrder)
      }
    }
  }
}

function assignCorrectiveWorkOrderCardData(
  workOrder: WorkOrderCardData,
  correctiveWorkList: CorrectiveWorkOnItem[]
) {
  let allWorkPerformed = true
  for (const correctiveWork of correctiveWorkList) {
    // To be in the `Performed` group all the CorrectivePerformedWork done in the WorkOrder
    // should be in the `accepted` state
    if (correctiveWork.conclusion && correctiveWork.conclusion !== "accepted") {
      allWorkPerformed = false
      break
    }
  }
  if (allWorkPerformed) {
    performedWorkOrderList.value.push(workOrder)
  } else {
    notPerformedWorkOrderList.value.push(workOrder)
  }
}

function getRightButtonText() {
  if (currentSelection.value) {
    return notPerformedWorkOrderList.value.includes(currentSelection.value)
      ? t("sfxButtons.btn_perform_work")
      : t("sfxButtons.btn_see_performed_work")
  } else {
    return t("sfxButtons.btn_to_work_orders")
  }
}

function clickCancel() {
  onDialogCancel()
}

async function openWorkOrder() {
  const workOrderId = currentSelection.value?.workOrderData.id

  const path = currentSelection.value?.isCorrectiveWork
    ? NamedPath.WORK_ORDER_CORRECTIVE
    : NamedPath.WORK_ORDER_REGULAR

  await router.push({ name: path, params: { workOrderId } })
}

// Direct the user to SelectWorkOrder page with the work orders filtered for the current item
async function goToWorkOrderList() {
  const filterStore = useFilterStore()
  filterStore.workOrderPageFilters.hasFilters = true
  filterStore.workOrderPageFilters.systemLink = {
    entityType: props.associationType,
    entityId: props.entityId,
    entityDesignation: props.entityName,
  }

  return router.push({
    name: NamedPath.SELECT_WORK_ORDER,
    query: {
      filters: JSON.stringify(filterStore.workOrderPageFilters),
    },
  })
}

async function goToPerformingWork() {
  // For performing corrective work, we do not know how to start performing work.
  // This is because there can be several issues inside the corrective work order,
  // which correspond/are related to the item.
  // Because we cannot determine which to start from, the user is transported to the work order corrective page,
  // on the issues tab, with issues related to the current item being highlighted.
  // For performing regular work, the only thing we would need is the work order id and the item id,
  // since in this case they are in 1 to 1 relation.
  // This allows us to directly start working on the planned regular work and as such, we route to there.

  if (!currentSelection.value?.workOrderData) {
    showErrorNotification(t("sfxErrorMsg.error_work_order_extract"))

    Log.error({
      message: "Failed to establish work order data",
      data: { selection: currentSelection.value },
    })
    return
  }

  if (currentSelection.value?.isCorrectiveWork) {
    return router.push({
      // Redirect the user to the CorrectiveWorkOrderOverview Issues tab
      // with the Issues related to this Item highlighted
      name: NamedPath.WORK_ORDER_CORRECTIVE,
      params: {
        workOrderId: currentSelection.value.workOrderData.id,
        itemId: currentSelection.value.issuesData[0].actionable_item,
      },
    })
  }

  const workDomain = await getWorkDomainWorkspaceData(
    currentSelection.value?.workOrderData.work_domain
  )

  if (workDomain?.require_proof_of_presence) {
    const result = await checkScan({
      entityType: props.associationType,
      entityName: props.entityName,
      entityId: props.entityId,
    })

    if (!result) {
      // User does not want to/failed to verify
      showErrorNotification(t("sfxErrorMsg.error_proof_of_presence_required"))
      return
    }
  }

  return router.push({
    name: NamedPath.PLANNED_REGULAR_WORK,
    // A RegularWorkOrder can have many Items but only one RegularWork per Item
    params: {
      itemId: props.entityId,
      workOrderId: currentSelection.value?.workOrderData.id,
    },
  })
}

async function seePerformedWork() {
  // This specifically is for work orders in which all the work has been performed.
  // Just like with the "performing" work, the corrective work can have multiple issues that are related to this item.
  // This means that we need to show the performed work page, filtered for this specific item and in state "completed".
  // For regular work, because the relation is 1 to 1, we can directly go to the performed regular work page.

  currentSelection.value?.isCorrectiveWork
    ? // TODO: Direct the user to SelectPerformedWork page with the performed work filtered for the selected work order
      showNotImplementedNotification()
    : await router.push({
        name: NamedPath.PERFORMED_REGULAR_WORK,
        // A RegularWorkOrder can have many Items but only one RegularWork per Item
        params: { performedWorkId: currentSelection.value?.performedWorkData[0].id },
      })
}

async function rightButtonFunction() {
  if (currentSelection.value) {
    await (notPerformedWorkOrderList.value.includes(currentSelection.value)
      ? goToPerformingWork()
      : seePerformedWork())
  } else {
    await goToWorkOrderList()
  }
}

function cardClick(workOrderClicked: WorkOrderCardData) {
  if (onlyOneWorkOrder.value) return

  currentSelection.value =
    currentSelection.value?.workOrderData.id === workOrderClicked.workOrderData.id
      ? undefined
      : workOrderClicked
}

function determineClass(workOrder: WorkOrderCardData) {
  return currentSelection.value?.workOrderData.id === workOrder.workOrderData.id &&
    !onlyOneWorkOrder.value
    ? "sfx-card--selected"
    : undefined
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin sfx-dialog-list" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "text-h3 q-pb-lg" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("sfxDialogListWorkOrder.title")), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1 q-pb-lg" }, {
            default: _withCtx(() => [
              _createElementVNode("span", { innerHTML: subtitle.value }, null, 8, _hoisted_1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "sfx-dialog-list-elements" }, {
            default: _withCtx(() => [
              (loadingData.value)
                ? (_openBlock(), _createBlock(SfxSkeleton, {
                    key: 0,
                    "no-of-cards": 3
                  }))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (notPerformedWorkOrderList.value?.length > 0)
                      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(notPerformedWorkOrderList.value, (workOrder) => {
                          return (_openBlock(), _createBlock(SfxCardWorkOrderForEntity, {
                            key: workOrder.workOrderData.id,
                            "work-order": workOrder,
                            onClick: ($event: any) => (cardClick(workOrder)),
                            class: _normalizeClass(determineClass(workOrder))
                          }, null, 8, ["work-order", "onClick", "class"]))
                        }), 128))
                      : _createCommentVNode("", true),
                    (performedWorkOrderList.value?.length > 0)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("b", null, _toDisplayString(_ctx.$t("sfxDialogListWorkOrder.performed")), 1)
                          ]),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(performedWorkOrderList.value, (workOrder) => {
                            return (_openBlock(), _createBlock(SfxCardWorkOrderForEntity, {
                              key: workOrder.workOrderData.id,
                              "work-order": workOrder,
                              onClick: ($event: any) => (cardClick(workOrder)),
                              class: _normalizeClass(determineClass(workOrder)),
                              workPerformed: ""
                            }, null, 8, ["work-order", "onClick", "class"]))
                          }), 128))
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 64))
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_actions, { class: "q-px-md q-pb-md" }, {
            default: _withCtx(() => [
              _createVNode(SfxBtnOutline, {
                onClick: leftButtonFunction.value,
                label: leftButtonText.value,
                class: "col q-mr-sm"
              }, null, 8, ["onClick", "label"]),
              _createVNode(SfxBtn, {
                onClick: rightButtonFunction,
                label: rightButtonText.value,
                class: "col"
              }, null, 8, ["label"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})